/**
 * Client-side view of brunel_engine.resolve_offering precedence.
 * Used to surface effective offering/source before dispatch without inventing policy.
 */
export type OfferingSource = 'task_override' | 'worker_preference' | 'auto';

export type ResolvedOffering = {
  readonly offeringKey: string | null;
  readonly source: OfferingSource;
  readonly ok: boolean;
  readonly error?: 'pinned_offering_unavailable' | 'node_has_no_runtimes' | 'invalid_input';
};

export function resolveOfferingView(input: {
  readonly taskOfferingKey?: string | null;
  readonly workerPreferenceKey?: string | null;
  readonly nodeOfferingKeys: readonly string[];
  readonly autoOfferingKey?: string | null;
}): ResolvedOffering {
  const onNode = (key: string) => input.nodeOfferingKeys.includes(key);

  if (input.taskOfferingKey) {
    if (onNode(input.taskOfferingKey)) {
      return { offeringKey: input.taskOfferingKey, source: 'task_override', ok: true };
    }
    return {
      offeringKey: input.taskOfferingKey,
      source: 'task_override',
      ok: false,
      error: 'pinned_offering_unavailable',
    };
  }

  if (input.workerPreferenceKey) {
    if (onNode(input.workerPreferenceKey)) {
      return { offeringKey: input.workerPreferenceKey, source: 'worker_preference', ok: true };
    }
    return {
      offeringKey: input.workerPreferenceKey,
      source: 'worker_preference',
      ok: false,
      error: 'pinned_offering_unavailable',
    };
  }

  if (input.autoOfferingKey) {
    return { offeringKey: input.autoOfferingKey, source: 'auto', ok: true };
  }
  return { offeringKey: null, source: 'auto', ok: false, error: 'node_has_no_runtimes' };
}

export function offeringSourceLabel(source: OfferingSource): string {
  switch (source) {
    case 'task_override': return 'task override';
    case 'worker_preference': return 'worker pin';
    case 'auto': return 'auto';
  }
}

/** Split a Brunel offering key `runtime/model/location` without guessing missing parts. */
export function parseOfferingKey(key: string | null | undefined): {
  readonly runtimeKey: string | null;
  readonly modelKey: string | null;
  readonly location: string | null;
} {
  const trimmed = key?.trim() ?? '';
  if (trimmed === '') return { runtimeKey: null, modelKey: null, location: null };
  const [runtimeKey, modelKey, location] = trimmed.split('/');
  return {
    runtimeKey: runtimeKey && runtimeKey !== '' ? runtimeKey : null,
    modelKey: modelKey && modelKey !== '' ? modelKey : null,
    location: location && location !== '' ? location : null,
  };
}
